import { Progress } from '@agro-club/agroclub-shared'
import { refetchFunc, useSingleEntity, useTableData } from 'modules/domain/common/hooks'
import { Deal, DealPeriod } from 'modules/domain/deal/types'
import { WarehouseMovement } from 'modules/domain/warehouses/types'
import { endpoints } from 'modules/endpoints'
import { createContext, useContext, useMemo } from 'react'
import { useParams } from 'react-router'
import ItemLoadingLayout from 'views/layouts/ItemLoadingLayout/ItemLoadingLayout'
import { useWarehouseMovements } from './useWarehouseMovements'
import { SampleWithRequests } from 'views/pages/Samples/types'
import { defaultListRequestParams } from 'views/components/TableFilters/data'

export type WhMovements = {
  list: WarehouseMovement[]
  last: WarehouseMovement
  listWithoutLast: WarehouseMovement[]
  refetch: refetchFunc
  progress: Progress
}

interface DealContextType {
  deal: Deal
  dealRefetch: refetchFunc
  whMovements: WhMovements
  dealPeriods?: DealPeriod[]
  progressDealPeriods: Progress
  dealPeriodRefetch: refetchFunc
  samplesInfo: {
    data?: SampleWithRequests[]
    progress: Progress
    refetch: refetchFunc
  }
}

export const DealContext = createContext<DealContextType>({
  // we dont allow deal be empty to avoid existings checks
  deal: {} as Deal,
  dealRefetch: async () => {
    return undefined
  },
  whMovements: {} as WhMovements,
  progressDealPeriods: Progress.IDLE,
  dealPeriodRefetch: async () => {
    return undefined
  },
  samplesInfo: {
    data: undefined,
    progress: Progress.IDLE,
    refetch: async () => {
      return undefined
    },
  },
})

export const useDealContext = () => {
  const context = useContext(DealContext)
  if (!context.deal.id) {
    throw new Error('DealContext outside of Provider')
  }

  return context
}

export function DealProvider({ children }: any) {
  const { id } = useParams()
  const [, deal, dealRefetch] = useSingleEntity<Deal>(endpoints.deal(id))
  const {
    data: dealPeriods,
    progress: progressDealPeriods,
    refetch: dealPeriodRefetch,
  } = useTableData<DealPeriod>(`${endpoints.dealPeriods()}?deal=${id}`, defaultListRequestParams, true)
  const { progress, list, last, listWithoutLast, refetch } = useWarehouseMovements(
    deal?.has_warehouse_movements ? deal?.id : undefined,
  )
  const [progressSamples, samples, refetchSamples] = useSingleEntity<SampleWithRequests[]>(
    deal ? endpoints.samplesForBid(deal?.sale_bid.id) : undefined,
  )

  const samplesInfo = useMemo(
    () => ({ progress: progressSamples, data: samples, refetch: refetchSamples }),
    [progressSamples, samples, refetchSamples],
  )

  const loading = () => <ItemLoadingLayout id={id} />

  const whMovements = useMemo(
    () => ({ progress, list, last, listWithoutLast, refetch }),
    [last, list, listWithoutLast, progress, refetch],
  )

  if (!deal) {
    return loading()
  }

  return (
    <DealContext.Provider
      value={{ deal, dealRefetch, whMovements, samplesInfo, dealPeriods, progressDealPeriods, dealPeriodRefetch }}
    >
      {children}
    </DealContext.Provider>
  )
}
